import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, CircularProgress } from '@mui/material';
import { SupplierService } from '../../services/supplier.service';
import { AuthService } from '../../services/auth.service';
import { SupplierCreateDTO } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';
import { WithholdingService } from '../../services/withholding.service';
import { SupplierWithholdingDefaults as WhtDefaults } from '../../interfaces/procurement/withholding.interface';
import SupplierWithholdingDefaults from './withholding/SupplierWithholdingDefaults';

export const SupplierForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  // A create that succeeded but whose defaults save failed switches to edit mode with the new id,
  // so a retry updates instead of creating a duplicate.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const effectiveId = id ?? createdId ?? undefined;
  const editing = Boolean(id);
  const [form, setForm] = useState<SupplierCreateDTO>({
    name: '', contactPerson: '', email: '', phone: '', address: '', taxRegistrationNo: '',
    institutionId: AuthService.getSelectedInstitution()!!,
  });
  const [wht, setWht] = useState<WhtDefaults>({});
  const [whtAvailable, setWhtAvailable] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    SupplierService.getById(id!).then(s => {
      setForm({
        name: s.name,
        contactPerson: s.contactPerson ?? '',
        email: s.email ?? '',
        phone: s.phone ?? '',
        address: s.address ?? '',
        taxRegistrationNo: s.taxRegistrationNo ?? '',
        institutionId: s.institutionId,
      });
      setWht({
        defaultVdsRuleId: s.defaultVdsRuleId ?? null,
        defaultTdsRuleId: s.defaultTdsRuleId ?? null,
        exemptionNote: s.withholdingExemptionNote ?? '',
      });
    }).catch(e => toast.showApiError(e, 'Failed to load supplier'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleField = (k: keyof SupplierCreateDTO) =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.showApiError(null, 'Name is required'); return; }
    try {
      setSaving(true);
      const saved = effectiveId ? await SupplierService.update(effectiveId, form) : await SupplierService.create(form);
      const supplierId = saved?.id ?? effectiveId;
      if (!effectiveId && supplierId) setCreatedId(supplierId);
      if (whtAvailable && supplierId) await WithholdingService.saveSupplierDefaults(supplierId, wht);
      toast.showSuccess(`Supplier ${effectiveId ? 'updated' : 'created'}`);
      navigate('/procurement/suppliers');
    } catch (e) {
      toast.showApiError(e, `Failed to ${effectiveId ? 'update' : 'create'} supplier`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box data-help-page="procurement.supplierForm.page" sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>{editing ? 'Edit Supplier' : 'New Supplier'}</Typography>
      <Paper data-help-section="procurement.supplierForm.section.main" sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField data-help="procurement.supplierForm.field.name" label="Name" required value={form.name} onChange={handleField('name')} inputProps={{ 'aria-label': 'Name' }} />
        <TextField data-help="procurement.supplierForm.field.contact" label="Contact Person" value={form.contactPerson} onChange={handleField('contactPerson')} />
        <TextField data-help="procurement.supplierForm.field.email" label="Email" type="email" value={form.email} onChange={handleField('email')} />
        <TextField data-help="procurement.supplierForm.field.phone" label="Phone" value={form.phone} onChange={handleField('phone')} />
        <TextField data-help="procurement.supplierForm.field.address" label="Address" multiline rows={2} value={form.address} onChange={handleField('address')} />
        <TextField data-help="procurement.supplierForm.field.taxNo" label="Tax Registration No" value={form.taxRegistrationNo} onChange={handleField('taxRegistrationNo')} />
        <SupplierWithholdingDefaults value={wht} onChange={setWht} onAvailability={setWhtAvailable} />
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button data-help="procurement.supplierForm.cancel" onClick={() => navigate(-1)}>Cancel</Button>
          <Button data-help="procurement.supplierForm.save" variant="contained" onClick={handleSubmit} disabled={saving}>Save</Button>
        </Box>
      </Paper>
    </Box>
  );
};
